import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BORDER_SOFT,
  BORDER_STRONG,
  BottomSheet,
  CONTROL,
  DESTRUCTIVE,
  FieldError,
  FieldLabel,
  Icon,
  RADIUS_INPUT,
  SegmentedControl,
  TAP,
  TEAL,
  TEAL_TINT,
  TEXT,
  TEXT_DIM,
  inputStyle,
  t,
} from "../../components/mobile";
import { getFloorPlanTables } from "../../services/floorPlan.service";
import {
  RESERVATION_STATUS_LABELS,
  ReservationStatus,
} from "../../services/tableReservations.service";

/**
 * Touch-layout building blocks shared by the three Table Reservations
 * screens. Everything here is composed from `src/components/mobile` — no
 * parallel primitives: `PickerField` is a 48px button plus the kit's
 * `BottomSheet`, `DateTimeField` is two native pickers with the kit's
 * `inputStyle`, `Stepper` is two 44px buttons around a numeric input.
 *
 * Nothing in this file is used by the desktop (>=1024) branches.
 */

export interface PickerOption {
  id: string;
  label: string;
  /** Second line under the label, e.g. "seats 2–6". */
  hint?: string;
  disabled?: boolean;
}

/** A list longer than this gets a pinned search box inside its sheet. */
const SEARCH_THRESHOLD = 6;

/**
 * A choice from a list: a 48px field that opens a bottom sheet of 48px rows.
 * Never a free-text input for an id, never an absolutely positioned dropdown
 * inside a scroll container.
 */
export const PickerField: React.FC<{
  language: any;
  label: string;
  /** Sheet heading; defaults to the label. */
  sheetTitle?: string;
  placeholder: string;
  options: PickerOption[];
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Shown as a muted line under the field (why it is empty / disabled). */
  hint?: string;
  error?: string;
  /** Adds a "clear" row at the top of the sheet with this label. */
  clearLabel?: string;
  loading?: boolean;
}> = ({
  language,
  label,
  sheetTitle,
  placeholder,
  options,
  value,
  onChange,
  required,
  disabled,
  hint,
  error,
  clearLabel,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.id === value);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint ?? "").toLowerCase().includes(q),
    );
  }, [options, query]);

  const showSearch = options.length > SEARCH_THRESHOLD;

  return (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        onClick={() => {
          setQuery("");
          setOpen(true);
        }}
        className="flex items-center font-inter"
        style={{
          ...inputStyle,
          // inputStyle is display:block for plain inputs; this one lays its
          // label and chevron out in a row.
          display: "flex",
          alignItems: "center",
          gap: "10px",
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.55 : 1,
          borderColor: error ? DESTRUCTIVE : BORDER_STRONG,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: selected ? TEXT : "#898989",
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="chevd" size={18} />
      </button>
      {error ? (
        <FieldError>{error}</FieldError>
      ) : hint ? (
        <p
          className="font-inter"
          style={{ marginTop: "6px", fontSize: "12.5px", color: TEXT_DIM }}
        >
          {hint}
        </p>
      ) : null}

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={sheetTitle ?? label}
        header={
          showSearch ? (
            <div style={{ padding: "0 20px 12px" }}>
              <div
                className="flex items-center"
                style={{
                  height: `${CONTROL}px`,
                  border: `1px solid ${BORDER_STRONG}`,
                  borderRadius: RADIUS_INPUT,
                  gap: "8px",
                  padding: "0 12px",
                }}
              >
                <Icon name="search" size={18} />
                <input
                  type="search"
                  autoFocus={false}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t(language, "Search")}
                  aria-label={t(language, "Search")}
                  className="font-inter placeholder:text-placeholder bg-transparent outline-none"
                  style={{
                    fontSize: "16px",
                    flex: 1,
                    minWidth: 0,
                    color: TEXT,
                    height: "100%",
                    border: "none",
                    WebkitAppearance: "none",
                  }}
                />
              </div>
            </div>
          ) : undefined
        }
      >
        {clearLabel && (
          <SheetRow
            label={clearLabel}
            selected={!value}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
          />
        )}
        {loading && (
          <p
            className="font-inter"
            style={{ padding: "16px 20px", fontSize: "14px", color: TEXT_DIM }}
          >
            {t(language, "Loading…")}
          </p>
        )}
        {!loading && visible.length === 0 && (
          <p
            className="font-inter"
            style={{ padding: "16px 20px", fontSize: "14px", color: TEXT_DIM }}
          >
            {t(language, "No results")}
          </p>
        )}
        {visible.map((option, index) => (
          <SheetRow
            key={option.id}
            label={option.label}
            hint={option.hint}
            selected={option.id === value}
            disabled={option.disabled}
            last={index === visible.length - 1}
            onClick={() => {
              onChange(option.id);
              setOpen(false);
            }}
          />
        ))}
      </BottomSheet>
    </div>
  );
};

/** One 48px row of a picker sheet: radio, label, optional hint, tint when selected. */
export const SheetRow: React.FC<{
  label: string;
  hint?: string;
  selected: boolean;
  disabled?: boolean;
  last?: boolean;
  onClick: () => void;
}> = ({ label, hint, selected, disabled, last, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onClick}
    className="flex items-center font-inter"
    style={{
      width: "100%",
      minHeight: `${CONTROL}px`,
      padding: "8px 20px",
      gap: "12px",
      textAlign: "left",
      border: "none",
      borderBottom: last ? undefined : `1px solid ${BORDER_SOFT}`,
      backgroundColor: selected ? TEAL_TINT : "transparent",
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "default" : "pointer",
    }}
  >
    <span
      style={{
        width: "20px",
        height: "20px",
        borderRadius: "999px",
        flex: "none",
        border: `${selected ? 6 : 1.5}px solid ${selected ? TEAL : BORDER_STRONG}`,
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
      }}
    />
    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: "block",
          fontSize: "15px",
          fontWeight: selected ? 500 : 400,
          color: TEXT,
        }}
      >
        {label}
      </span>
      {hint && (
        <span style={{ display: "block", fontSize: "12.5px", color: TEXT_DIM }}>
          {hint}
        </span>
      )}
    </span>
  </button>
);

/**
 * Date + time as two native pickers side by side, plus Today / Tomorrow
 * chips. Native `date`/`time` inputs open the platform wheel on iOS and
 * Android — one tap, no free-text parsing, no calendar popover inside the
 * page's scroll container.
 */
export const DateTimeField: React.FC<{
  language: any;
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  required?: boolean;
  error?: string;
  /** Only dates from today on (the create screen's rule). */
  futureOnly?: boolean;
}> = ({ language, label, value, onChange, required, error, futureOnly }) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  const dateValue = value
    ? `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
    : "";
  const timeValue = value ? `${pad(value.getHours())}:${pad(value.getMinutes())}` : "";

  const setDatePart = (raw: string) => {
    if (!raw) {
      onChange(null);
      return;
    }
    const [y, m, d] = raw.split("-").map(Number);
    const next = value ? new Date(value) : new Date();
    if (!value) next.setHours(19, 0, 0, 0);
    next.setFullYear(y, m - 1, d);
    onChange(next);
  };

  const setTimePart = (raw: string) => {
    if (!raw) return;
    const [h, min] = raw.split(":").map(Number);
    const next = value ? new Date(value) : new Date();
    next.setHours(h, min, 0, 0);
    onChange(next);
  };

  const jumpDay = (offset: number) => {
    const next = value ? new Date(value) : new Date();
    if (!value) next.setHours(19, 0, 0, 0);
    const base = new Date();
    base.setDate(base.getDate() + offset);
    next.setFullYear(base.getFullYear(), base.getMonth(), base.getDate());
    onChange(next);
  };

  const today = new Date();
  const minDate = futureOnly
    ? `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
    : undefined;

  const chip = (label: string, offset: number) => {
    const target = new Date();
    target.setDate(target.getDate() + offset);
    const active =
      !!value &&
      value.getFullYear() === target.getFullYear() &&
      value.getMonth() === target.getMonth() &&
      value.getDate() === target.getDate();
    return (
      <button
        type="button"
        onClick={() => jumpDay(offset)}
        className="font-inter"
        style={{
          height: `${TAP}px`,
          padding: "0 16px",
          borderRadius: "30px",
          fontSize: "14px",
          fontWeight: 500,
          whiteSpace: "nowrap",
          border: `1px solid ${active ? TEAL : BORDER_STRONG}`,
          backgroundColor: active ? TEAL : "#FFFFFF",
          color: active ? "#FFFFFF" : TEXT,
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex items-center" style={{ gap: "8px" }}>
        <input
          type="date"
          value={dateValue}
          min={minDate}
          onChange={(e) => setDatePart(e.target.value)}
          aria-label={t(language, "Date")}
          className="font-inter"
          style={{
            ...inputStyle,
            flex: "1 1 0",
            minWidth: 0,
            borderColor: error ? DESTRUCTIVE : BORDER_STRONG,
          }}
        />
        <input
          type="time"
          value={timeValue}
          step={900}
          onChange={(e) => setTimePart(e.target.value)}
          aria-label={t(language, "Time")}
          className="font-inter"
          style={{
            ...inputStyle,
            flex: "0 0 128px",
            width: "128px",
            borderColor: error ? DESTRUCTIVE : BORDER_STRONG,
          }}
        />
      </div>
      <div className="flex items-center" style={{ gap: "8px", marginTop: "8px" }}>
        {chip(t(language, "Today"), 0)}
        {chip(t(language, "Tomorrow"), 1)}
      </div>
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

/** Party size as a −/+ stepper around a numeric field. */
export const Stepper: React.FC<{
  language: any;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  required?: boolean;
  error?: string;
}> = ({ language, label, value, onChange, min = 1, max = 500, required, error }) => {
  const numeric = parseInt(value, 10);
  const step = (delta: number) => {
    const base = Number.isFinite(numeric) ? numeric : min - delta;
    const next = Math.min(max, Math.max(min, base + delta));
    onChange(String(next));
  };
  const button = (label: string, delta: number, disabled: boolean, glyph: string) => (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => step(delta)}
      className="flex items-center justify-center font-inter"
      style={{
        width: `${CONTROL}px`,
        height: `${CONTROL}px`,
        flex: "none",
        borderRadius: RADIUS_INPUT,
        border: `1px solid ${BORDER_STRONG}`,
        backgroundColor: "#FFFFFF",
        color: TEXT,
        fontSize: "22px",
        lineHeight: "22px",
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {glyph}
    </button>
  );

  return (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex items-center" style={{ gap: "8px" }}>
        {button(t(language, "Decrease party size"), -1, Number.isFinite(numeric) && numeric <= min, "−")}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
          aria-label={label}
          className="font-inter"
          style={{
            ...inputStyle,
            flex: "1 1 0",
            minWidth: 0,
            textAlign: "center",
            fontWeight: 500,
            borderColor: error ? DESTRUCTIVE : BORDER_STRONG,
          }}
        />
        {button(t(language, "Increase party size"), 1, Number.isFinite(numeric) && numeric >= max, "+")}
      </div>
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

/**
 * A status choice. Up to four options are a row of 44px pills; more than that
 * is a 48px field opening a sheet of 48px rows — a seven-way pill row does not
 * fit a phone and degrades into a sideways scroller whose last options (here:
 * Cancelled, No Show, Completed) are off-screen behind a fade.
 *
 * `pillsFit` is the caller's width test: on the 700–1023px tablet the
 * front-of-house iPad runs there is room for the pills, and a one-tap row
 * beats a sheet. They wrap onto a second line there rather than scrolling
 * sideways, so a longer translation can never hide an option.
 */
export const StatusChoice: React.FC<{
  /** Names the control: the pill row's aria-label, and the field's own text. */
  label: string;
  /** Sheet heading; defaults to the label. */
  sheetTitle?: string;
  /** Already translated. */
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  /** Render the pill row even above the four-option limit (tablet widths). */
  pillsFit?: boolean;
}> = ({ label, sheetTitle, options, value, onChange, pillsFit }) => {
  const [open, setOpen] = useState(false);

  if (pillsFit || options.length <= 4) {
    return (
      <SegmentedControl
        options={options}
        value={value}
        onChange={onChange}
        wrap={options.length > 4}
        ariaLabel={label}
      />
    );
  }

  const selected = options.find((option) => option.value === value);

  return (
    <div>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={label}
        onClick={() => setOpen(true)}
        className="flex items-center font-inter"
        style={{
          ...inputStyle,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            flex: "none",
            fontSize: "13px",
            color: TEXT_DIM,
          }}
        >
          {label}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 500,
            color: TEXT,
          }}
        >
          {selected?.label ?? ""}
        </span>
        <Icon name="chevd" size={18} />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={sheetTitle ?? label}>
        {options.map((option, index) => (
          <SheetRow
            key={option.value || "all"}
            label={option.label}
            selected={option.value === value}
            last={index === options.length - 1}
            onClick={() => {
              setOpen(false);
              if (option.value !== value) onChange(option.value);
            }}
          />
        ))}
      </BottomSheet>
    </div>
  );
};

/** The six `ReservationStatus` values as SegmentedControl options. */
export const statusSegments = (language: any) =>
  (Object.keys(RESERVATION_STATUS_LABELS) as unknown as ReservationStatus[]).map((key) => ({
    value: String(key),
    label: t(language, RESERVATION_STATUS_LABELS[key as ReservationStatus]),
  }));

/** Why a location's table list came back empty. */
export type TableLoadError =
  /** The endpoint 404s: this location has no floor plan at all. */
  | "no-floor-plan"
  /** Anything else — a 500, a dropped connection, an expired token. */
  | "failed";

/** Rows per `/floor-plans-details/list` request. */
const FLOOR_PLAN_PAGE_SIZE = 200;
/** Hard stop so a broken `count` can never spin the loop. */
const FLOOR_PLAN_MAX_PAGES = 10;

/**
 * Loads a location's tables (floor_plan_details of type "table") as picker
 * options labelled with the covers range. Never throws: a 404 (the answer for
 * a location with no floor plan) and a real failure are both reported through
 * `error`, which the screens word differently — telling a host their venue has
 * no floor plan because the network dropped sends them to the wrong place.
 *
 * A plan's rows are paged through rather than capped at one request: walls,
 * doors and labels share the table with the tables, and the `type === "table"`
 * filter runs client-side, so a large plan's last tables would otherwise be
 * invisible in the picker (and unresolvable on a booking that already uses one).
 */
export const useLocationTables = (locationId: string, language: any) => {
  const [tables, setTables] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<TableLoadError | null>(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!locationId) {
      setTables([]);
      setError(null);
      return;
    }
    const request = ++requestRef.current;
    setLoading(true);
    setError(null);

    const rowsOf = (res: any): any[] => res?.data?.data?.data ?? [];
    const countOf = (res: any): number => Number(res?.data?.data?.count ?? 0);

    const loadAll = async () => {
      const first = await getFloorPlanTables(locationId, FLOOR_PLAN_PAGE_SIZE, 1);
      let rows = rowsOf(first);
      const count = countOf(first);
      const pages = Math.min(
        FLOOR_PLAN_MAX_PAGES,
        Number.isFinite(count) && count > 0
          ? Math.ceil(count / FLOOR_PLAN_PAGE_SIZE)
          : 1,
      );
      for (let page = 2; page <= pages && rows.length < count; page += 1) {
        const next = await getFloorPlanTables(locationId, FLOOR_PLAN_PAGE_SIZE, page);
        const nextRows = rowsOf(next);
        if (nextRows.length === 0) break;
        rows = rows.concat(nextRows);
      }
      return rows;
    };

    loadAll()
      .then((rows) => {
        if (request !== requestRef.current) return;
        setTables(
          rows
            .filter((row) => String(row?.type ?? "").toLowerCase() === "table")
            .map((row) => ({
              id: row.id,
              label: row.name,
              hint: coversHint(language, row.minCovers, row.maxCovers),
            })),
        );
      })
      .catch((err: any) => {
        if (request !== requestRef.current) return;
        setTables([]);
        setError(err?.response?.status === 404 ? "no-floor-plan" : "failed");
      })
      .finally(() => {
        if (request === requestRef.current) setLoading(false);
      });
  }, [locationId, language]);

  return { tables, loading, error };
};

/**
 * The hint under a Table picker when it has nothing to offer. A 404 means the
 * location genuinely has no floor plan; any other failure says so instead of
 * blaming the venue's setup.
 */
export const tableLoadHint = (
  language: any,
  error: TableLoadError | null,
  isEmpty: boolean,
): string | undefined => {
  if (error === "no-floor-plan") {
    return t(language, "This location has no floor plan yet");
  }
  if (error === "failed") {
    return t(language, "Could not load this location's tables");
  }
  if (isEmpty) return t(language, "This location has no tables yet");
  return undefined;
};

/**
 * The option that stands in for a table the floor plan no longer returns
 * (deleted, moved to another plan, or a plan that failed to load). The field
 * must never print the stored id: a `floor_plan_details` UUID means nothing to
 * a host, and it reads as data corruption. Naming the situation instead keeps
 * the assignment visible and leaves the user free to pick a current table.
 */
export const unresolvedTableOption = (language: any, id: string): PickerOption => ({
  id,
  label: t(language, "Table no longer available"),
});

/** "seats 2–6" / "seats 4" / "" from a table's covers columns. */
export const coversHint = (
  language: any,
  minCovers: number | null | undefined,
  maxCovers: number | null | undefined,
): string | undefined => {
  const min = Number(minCovers);
  const max = Number(maxCovers);
  const hasMin = Number.isFinite(min) && min > 0;
  const hasMax = Number.isFinite(max) && max > 0;
  if (!hasMin && !hasMax) return undefined;
  if (hasMin && hasMax && min !== max) return `${t(language, "seats")} ${min}–${max}`;
  return `${t(language, "seats")} ${hasMax ? max : min}`;
};

/** dd.MM.yyyy HH:mm, or "-". Shared by the list, the create and the detail screens. */
export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** HH:mm of a reservation instant. */
export const formatTime = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** dd.MM of a reservation instant. */
export const formatDayMonth = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
};

/**
 * The touch form grid: one column on a phone, two on a tablet. Children are
 * the Field/PickerField blocks, which carry their own 16px bottom margin.
 */
export const FormGrid: React.FC<{ isTablet: boolean; children: React.ReactNode }> = ({
  isTablet,
  children,
}) => (
  <div
    style={
      isTablet
        ? {
            display: "grid",
            // minmax(0,…) — a plain `1fr` track still floors at the item's
            // min-content width, which a nowrap field label or value can push
            // past half the row and out of the viewport.
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            columnGap: "20px",
          }
        : undefined
    }
  >
    {children}
  </div>
);

/** A block that spans both columns of `FormGrid` (Notes, section headings). */
export const FormGridFull: React.FC<{ isTablet: boolean; children: React.ReactNode }> = ({
  isTablet,
  children,
}) => <div style={isTablet ? { gridColumn: "1 / -1" } : undefined}>{children}</div>;
